import { useContext, useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import BaseInputField from "../../../components/elements/BaseInputField"
import BaseInputSelection from "../../../components/elements/BaseInputSelection"
import BaseModal from "../../../components/layout/BaseModal"
import { ModalContext } from "../../../components/contexts/ModalContext"
import { GeneralQueryKeys } from "../../../api/constants"
import { getObjectsForTourStops } from "../api/apiCalls"
import { TourObjectRef, TourStop, TourWeekday, TOUR_WEEKDAYS } from "../types"

type AddStopModalType = {
    modalId: number
    defaultWeekday: TourWeekday
    usedObjectIds: string[]
    onSave: (data: { object: TourObjectRef; weekday: TourWeekday; adHoc?: TourStop["adHocTask"] }) => void
}

export default function AddStopModal(props: AddStopModalType) {
    const modalCon = useContext(ModalContext)
    const { data: objects, isLoading } = useQuery({
        queryKey: [GeneralQueryKeys.OBJECTS_ADDRESS_ONLY + "tour"],
        queryFn: getObjectsForTourStops,
    })
    const [objectId, setObjectId] = useState("")
    const [weekday, setWeekday] = useState<TourWeekday>(props.defaultWeekday)
    const [adHocKind, setAdHocKind] = useState<"plan" | "openTask" | "newTask">("plan")
    const [adHocText, setAdHocText] = useState("")

    const available = useMemo(
        () => (objects ?? []).filter((item) => !props.usedObjectIds.includes(item._id) || adHocKind !== "plan"),
        [objects, props.usedObjectIds, adHocKind],
    )

    const reset = () => {
        setObjectId("")
        setWeekday(props.defaultWeekday)
        setAdHocKind("plan")
        setAdHocText("")
    }

    const addStop = () => {
        const object = (objects ?? []).find((item) => item._id === objectId)
        if (!object) return
        if (adHocKind !== "plan" && !adHocText.trim()) return
        props.onSave({
            object,
            weekday,
            adHoc:
                adHocKind === "plan"
                    ? undefined
                    : {
                          _id: `adhoc-${Date.now()}`,
                          activity: adHocText.trim(),
                          kind: adHocKind,
                      },
        })
        reset()
        modalCon?.trigger(props.modalId)
    }

    return (
        <BaseModal
            modalId={props.modalId}
            size="fit"
            title="Objekt zur Tour"
            onOpen={() => {
                reset()
                setWeekday(props.defaultWeekday)
            }}
            bottomRow={
                <div className="flex justify-end w-full">
                    <button className="btn btn-primary" type="button" disabled={!objectId || (adHocKind !== "plan" && !adHocText.trim())} onClick={addStop}>
                        Hinzufügen
                    </button>
                </div>
            }>
            <p className="text-sm text-gray-500 px-2 pt-4 pb-2">Planstopp oder außerplanmäßiger Einsatz (bestehende offene Aufgabe bzw. neue Einzelaufgabe).</p>
            <BaseInputSelection
                label="Objekt *"
                placeholder="Auswählen"
                data={available}
                value={objectId || undefined}
                setId={setObjectId}
                searchable={false}
                customLoading={isLoading}
            />
            <div className="flex flex-col w-full px-2">
                <label className="text-sm pl-2 pb-1">Wochentag *</label>
                <div className="flex flex-wrap gap-2">
                    {TOUR_WEEKDAYS.map((day) => (
                        <button
                            key={day.value}
                            type="button"
                            className={`btn btn-sm ${weekday === day.value ? "btn-primary" : "btn-ghost bg-gray-100"}`}
                            onClick={() => setWeekday(day.value)}>
                            {day.short}
                        </button>
                    ))}
                </div>
            </div>
            <div className="flex flex-col gap-2 px-2 pt-2">
                <label className="text-sm pl-2">Art</label>
                <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" className="radio radio-sm" checked={adHocKind === "plan"} onChange={() => setAdHocKind("plan")} />
                    <span className="text-sm">Fahrplan (wiederkehrend)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" className="radio radio-sm" checked={adHocKind === "openTask"} onChange={() => setAdHocKind("openTask")} />
                    <span className="text-sm">Offene Aufgabe diesem Einsatz zuordnen</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" className="radio radio-sm" checked={adHocKind === "newTask"} onChange={() => setAdHocKind("newTask")} />
                    <span className="text-sm">Neue Einzelaufgabe</span>
                </label>
            </div>
            {adHocKind !== "plan" && (
                <div className="mt-2">
                    <BaseInputField
                        label={adHocKind === "openTask" ? "Offene Aufgabe (Platzhalter) *" : "Einzelaufgabe *"}
                        type="text"
                        placeholder={adHocKind === "openTask" ? "z. B. Fensterreinigung KW 42" : "z. B. Sonderreinigung Eingang"}
                        onChange={setAdHocText}
                    />
                </div>
            )}
        </BaseModal>
    )
}
